import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { userService } from '../services/userService';
import { getAvatarUrl } from '../utils/avatarUtils';

const NavContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #334155;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 16px;
  }
  
  border-radius: 0;
  color: white;
  font-size: 14px;
  padding: 20px 48px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: white;
  font-family: "Playwrite DE SAS", cursive;
  font-size: 42px;
  font-weight: 700;
  letter-spacing: -1px;
  line-height: 1;
  
  &:hover {
    color: #0066ff;
  }
`;

const NavLinks = styled.div`
  display: none;
  align-items: center;
  gap: 60px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  
  @media (min-width: 768px) {
    display: flex;
  }
`;

const NavLink = styled(Link)`
  position: relative;
  height: 40px;
  color: white;
  background-color: transparent;
  text-decoration: none;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.3px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 0;
  
  &:hover {
    background-color: white;
    color: black;
  }
`;


const ButtonGroup = styled.div`
  display: none;
  align-items: center;
  gap: 16px;
  
  @media (min-width: 768px) {
    display: flex;
  }
`;

const ContactButton = styled.button`
  border: 1px solid #475569;
  background: transparent;
  color: white;
  padding: 8px 16px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #1e293b;
  }
`;

const GetStartedButton = styled.button`
  background: white;
  color: black;
  padding: 8px 16px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0px 0px 30px 7px rgba(255, 255, 255, 0.5);
  
  &:hover {
    background-color: #f1f5f9;
    box-shadow: 0px 0px 30px 14px rgba(255, 255, 255, 0.5);
  }
`;

const MobileMenuButton = styled.button`
  display: block;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  
  @media (min-width: 768px) {
    display: none;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 120px;
  left: 0;
  background: black;
  width: 100%;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border-radius: 0;
  z-index: 1000;
`;

const MobileNavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.2px;
  transition: color 0.2s ease;
  
  &:hover {
    color: #818cf8;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 0;
  background-color: black;
  color: white;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: white;
    color: black;
  }
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const AvatarPlaceholder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #60a5fa;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const Username = styled.span`
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: inherit;
  letter-spacing: -0.2px;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: black;
  backdrop-filter: blur(10px);
  border: 1px solid #334155;
  border-radius: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  min-width: 200px;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  margin-top: 8px;
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 12px 16px;
  color: white;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.2s ease;
  border-bottom: 1px solid #334155;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: white;
    color: black;
  }
`;

const LogoutButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  color: white;
  text-align: left;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: white;
    color: black;
  }
`;

const LoginButton = styled(Link)`
  background-color: transparent;
  color: white;
  padding: 12px 24px;
  border: 1px solid #475569;
  border-radius: 0;
  text-decoration: none;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.2px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #1e293b;
  }
`;

const RegisterButton = styled(Link)`
  background: white;
  color: black;
  padding: 12px 24px;
  border-radius: 0;
  text-decoration: none;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.2px;
  transition: all 0.3s ease;
  box-shadow: 0px 0px 30px 7px rgba(255, 255, 255, 0.5);
  
  &:hover {
    background-color: #f1f5f9;
    box-shadow: 0px 0px 30px 14px rgba(255, 255, 255, 0.5);
  }
`;

interface NavigationProps {
  isAuthenticated: boolean;
  user?: {
    id: string;
    username: string;
    email: string;
    avatar?: string;
  };
  onLogout?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ isAuthenticated, user, onLogout }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      if (onLogout) {
        await onLogout();
      } else {
        await userService.logout();
      }
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <NavContainer>
      <Logo to="/">Accord</Logo>
      
      <NavLinks>
        <NavLink to="/wallpaper">Generate</NavLink>
        <NavLink to="/search">Search</NavLink>
      </NavLinks>

      <ButtonGroup>
        {isAuthenticated ? (
          <UserSection>
            <UserInfo onClick={() => setIsDropdownOpen(!isDropdownOpen)} ref={dropdownRef}>
              <Avatar src={getAvatarUrl(user?.avatar)} alt="User Avatar" />
              <Username>{user?.username || 'User'}</Username>
            </UserInfo>
            
            <DropdownMenu $isOpen={isDropdownOpen}>
              <DropdownItem to="/profile">Profile</DropdownItem>
              <DropdownItem to="/history">History</DropdownItem>
              <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
            </DropdownMenu>
          </UserSection>
        ) : (
          <>
            <LoginButton to="/login">Login</LoginButton>
            <RegisterButton to="/register">Get Started</RegisterButton>
          </>
        )}
      </ButtonGroup>

      <MobileMenuButton onClick={toggleMobileMenu}>
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </MobileMenuButton>

      <MobileMenu $isOpen={isMobileMenuOpen}>
        <MobileNavLink to="/wallpaper">Generate</MobileNavLink>
        <MobileNavLink to="/search">Search</MobileNavLink>
        {isAuthenticated ? (
          <>
            <MobileNavLink to="/profile">Profile</MobileNavLink>
            <MobileNavLink to="/history">History</MobileNavLink>
            <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
          </>
        ) : (
          <>
            <LoginButton to="/login">Login</LoginButton>
            <RegisterButton to="/register">Get Started</RegisterButton>
          </>
        )}
      </MobileMenu>
    </NavContainer>
  );
};

export default Navigation;